import { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { UserCog, Search, Plus, Mail, Shield, Calendar, Trash2, MoreVertical, AlertCircle, Key, Eye, EyeOff, X } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const AdminList = () => {
  const { token, admin: currentAdmin } = useAdminAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  
  // Password change modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    fetchAdmins();
  }, []);

  useEffect(() => {
    const handleClickOutside = () => setActionMenuOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (admin) => {
    // Prevent deleting yourself
    if (admin.email === currentAdmin?.email) {
      alert('Você não pode excluir sua própria conta.');
      return;
    }

    const confirmMessage = admin.is_super_admin
      ? `Tem certeza que deseja excluir o Super Admin "${admin.full_name}"?\n\nEsta ação não pode ser desfeita.`
      : `Tem certeza que deseja excluir o administrador "${admin.full_name}"?\n\nEsta ação não pode ser desfeita.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeleteLoading(admin.id);
    try {
      await axios.delete(`${API}/admin/admins/${admin.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(admins.filter(a => a.id !== admin.id));
      alert('Administrador excluído com sucesso!');
    } catch (error) {
      console.error('Error deleting admin:', error);
      alert(error.response?.data?.detail || 'Erro ao excluir administrador');
    } finally {
      setDeleteLoading(null);
      setActionMenuOpen(null);
    }
  };

  const filteredAdmins = admins.filter(admin =>
    admin.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    admin.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('A nova senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('As senhas não coincidem');
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.post(
        `${API}/admin/auth/change-password`,
        {
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPasswordSuccess('Senha alterada com sucesso!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setPasswordLoading(false);
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordSuccess('');
    setShowPasswords({ current: false, new: false, confirm: false });
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <UserCog className="text-arcano-blue" />
            Administradores
          </h1>
          <p className="text-gray-600 text-sm md:text-base">Gerencie os administradores do sistema</p>
        </div>
        <div className="flex items-center gap-2 md:gap-3 w-full sm:w-auto">
          {currentAdmin?.is_super_admin && (
            <button
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center gap-2 bg-gray-600 text-white px-3 md:px-4 py-2 rounded-md hover:bg-gray-700 transition-colors text-sm md:text-base flex-1 sm:flex-none justify-center"
              data-testid="change-password-btn"
            >
              <Key size={18} />
              <span className="hidden sm:inline">Alterar Senha</span>
              <span className="sm:hidden">Senha</span>
            </button>
          )}
          {currentAdmin?.is_super_admin && (
            <Link
              to="/admin/admins/new"
              className="flex items-center gap-2 bg-arcano-blue text-white px-3 md:px-4 py-2 rounded-md hover:bg-arcano-blue-dark transition-colors text-sm md:text-base flex-1 sm:flex-none justify-center"
            >
              <Plus size={18} />
              <span className="hidden sm:inline">Novo Admin</span>
              <span className="sm:hidden">Novo</span>
            </Link>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
          />
        </div>
      </div>

      {/* Admins Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-arcano-blue mx-auto"></div>
        </div>
      ) : filteredAdmins.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <UserCog size={48} className="mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">Nenhum administrador encontrado</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAdmins.map((admin) => {
            const isCurrentUser = admin.email === currentAdmin?.email;
            
            return (
              <div key={admin.id} className="bg-white rounded-lg shadow-md p-6 relative">
                {/* Action Menu - Only show for super admins and not for current user */}
                {currentAdmin?.is_super_admin && !isCurrentUser && (
                  <div className="absolute top-4 right-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActionMenuOpen(actionMenuOpen === admin.id ? null : admin.id);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                      disabled={deleteLoading === admin.id}
                    >
                      {deleteLoading === admin.id ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-arcano-blue"></div>
                      ) : (
                        <MoreVertical size={20} className="text-gray-500" />
                      )}
                    </button>
                    
                    {actionMenuOpen === admin.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                        <div className="py-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(admin);
                            }}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                            Excluir Administrador
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full ${admin.is_super_admin ? 'bg-yellow-100' : 'bg-arcano-blue/10'}`}>
                    <Shield size={24} className={admin.is_super_admin ? 'text-yellow-600' : 'text-arcano-blue'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate">{admin.full_name}</h3>
                      {isCurrentUser && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium">
                          Você
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                      <Mail size={14} />
                      <span className="truncate">{admin.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                      <Calendar size={14} />
                      <span>{new Date(admin.created_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {admin.is_super_admin && (
                        <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-semibold">
                          Super Admin
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        admin.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {admin.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info box */}
      {currentAdmin?.is_super_admin && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle size={20} className="text-blue-500 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Dica de segurança</p>
            <p>Apenas Super Admins podem criar ou excluir administradores. Você não pode excluir sua própria conta.</p>
          </div>
        </div>
      )}

      {/* Total */}
      <div className="text-right text-gray-600">
        Total: {filteredAdmins.length} administrador(es)
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4" data-testid="change-password-modal">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Key className="text-arcano-blue" size={24} />
                Alterar Minha Senha
              </h2>
              <button
                onClick={closePasswordModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <form onSubmit={handlePasswordChange} className="p-6 space-y-4">
              {passwordError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
                  {passwordSuccess}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Senha Atual
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent pr-10"
                    required
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent pr-10"
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Mínimo de 6 caracteres</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent pr-10"
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closePasswordModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="flex-1 px-4 py-2 bg-arcano-blue text-white rounded-md hover:bg-arcano-blue-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-password-change-btn"
                >
                  {passwordLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Key size={18} />
                      Alterar Senha
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminList;
